import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form, Message, Label, Icon } from 'semantic-ui-react';
import { Field, reduxForm } from 'redux-form';
import ValidatedFormInput from 'components/ValidatedFormInput';
import {
  showCreateDiscountRuleModal,
  showEditDiscountRuleModal,
  saveDiscountRule,
  setEditorErrorMessage,
} from 'state/discountRule/actions';
import { requiredFieldValidation, rangeValidation } from 'utils/formUtils';

export const validate = values => {
  const errors = {};

  const discountPercentageRequired = requiredFieldValidation(
    values.discount_percentage,
    'Discount percentage'
  );
  if (discountPercentageRequired) {
    errors.discount_percentage = discountPercentageRequired;
  } else {
    const rangeError = rangeValidation(values.discount_percentage, 0, 100);
    if (rangeError) {
      errors.discount_percentage = rangeError;
    }
  }

  return errors;
};

class DiscountRuleEditorModal extends React.Component {
  onClose = () => {
    const { dispatch, isEditMode, reset } = this.props;
    dispatch(setEditorErrorMessage(null));
    if (isEditMode) {
      dispatch(showEditDiscountRuleModal(false, null));
    } else {
      dispatch(showCreateDiscountRuleModal(false));
    }
    reset();
  };

  onSubmit = values => {
    const { dispatch, isEditMode, editingRule } = this.props;

    const payload = {
      discount_percentage: parseFloat(values.discount_percentage),
      subscription_period_months_left: values.subscription_period_months_left,
    };

    if (isEditMode && editingRule) {
      payload.id = editingRule.id;
    }

    dispatch(saveDiscountRule(payload));
  };

  render() {
    const {
      isOpen,
      isEditMode,
      isLoading,
      handleSubmit,
      pristine,
      errorMessage,
    } = this.props;

    return (
      <Modal open={isOpen} onClose={this.onClose} size="small">
        <Modal.Header>
          {isEditMode ? 'Edit Renewal Discount' : 'Create Renewal Discount'}
        </Modal.Header>
        <Modal.Content>
          {errorMessage && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{errorMessage}</p>
            </Message>
          )}
          <Form onSubmit={handleSubmit(this.onSubmit)}>
            <Form.Field>
              <label>Discount Type</label>
              <div
                style={{
                  padding: '0.6em 0.8em',
                  background: '#f9fafb',
                  borderRadius: '4px',
                  border: '1px solid rgba(34,36,38,.15)',
                }}
              >
                <Label color="blue">Renewal</Label>
              </div>
            </Form.Field>
            <Form.Field>
              <label>Applies To</label>
              <div
                style={{
                  padding: '0.6em 0.8em',
                  background: '#f9fafb',
                  borderRadius: '4px',
                  border: '1px solid rgba(34,36,38,.15)',
                }}
              >
                <Label color="green">All Base Packages</Label>
              </div>
              <p
                style={{
                  color: '#888',
                  fontSize: '0.85em',
                  marginTop: '0.4em',
                }}
              >
                This discount will apply to all subscription package types
              </p>
            </Form.Field>
            <Form.Field>
              <label>
                <Icon name="calendar outline" />
                Months Left Rule
              </label>
              <Field
                name="subscription_period_months_left"
                component={ValidatedFormInput}
                placeholder="Enter number of months"
                type="number"
                label={{ basic: true, content: 'months' }}
                labelPosition="right"
              />
              <p
                style={{
                  color: '#888',
                  fontSize: '0.85em',
                  marginTop: '0.4em',
                }}
              >
                Discount applies when remaining subscription ≤ this value
              </p>
            </Form.Field>
            <Form.Field>
              <label>
                <Icon name="percent" />
                Discount Percentage
              </label>
              <Field
                name="discount_percentage"
                component={ValidatedFormInput}
                placeholder="Enter discount percentage"
                type="number"
                step="0.01"
                label={{ basic: true, content: '%' }}
                labelPosition="right"
                required
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit(this.onSubmit)}
            disabled={pristine || isLoading}
            loading={isLoading}
          >
            {isEditMode ? 'Update Discount' : 'Create Discount'}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

DiscountRuleEditorModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  editingRule: PropTypes.object,
  reset: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
};

DiscountRuleEditorModal.defaultProps = {
  editingRule: null,
  errorMessage: null,
};

const mapStateToProps = (state, ownProps) => {
  const { isEditMode, editingRule } = ownProps;

  let initialValues = {
    discount_percentage: '',
    subscription_period_months_left: '',
  };

  if (isEditMode && editingRule) {
    initialValues = {
      discount_percentage: editingRule.discount_percentage || '',
      subscription_period_months_left:
        editingRule.subscription_period_months_left || '',
    };
  }

  return {
    initialValues,
    enableReinitialize: true,
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'discountRuleEditor',
    validate,
  })(DiscountRuleEditorModal)
);
